import React, { useState } from "react";
import axios from "axios";
import { BASE_URL, SCRAPING_BASE_URL } from "../utils";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
const ReportPage = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [court, setCourt] = useState("");
  const [proceeding, setProceeding] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [results, setResults] = useState([]);

  const [showConfirm, setShowConfirm] = useState(false); // Confirmation popup
  const [showResultsPopup, setShowResultsPopup] = useState(false); // Final results popup

  const handleGenerateReport = async () => {
    if (!startDate || !endDate || !court || !proceeding) {
      alert("⚠️ All fields are required.");
      return;
    }

    // if (court !== "Kings County Surrogate's Court") {
    //   alert("⚠️ Currently, only 'Kings County Surrogates Court' is supported.");
    //   return;
    // }

    // if (proceeding !== "ADMINISTRATION PETITION") {
    //   alert("⚠️ Currently, only 'Administration Petition' is allowed.");
    //   return;
    // }

    setLoading(true);
    try {
      const res = await axios.post(`${SCRAPING_BASE_URL}/api/pie`, {
        startDate,
        endDate,
        court,
        proceeding,
      });

      const { status, message, stderr } = res.data;
      if (status === "success") {
        alert("✅ Report generated successfully.\n" + (message || ""));
      } else {
        let errorMsg = "❌ Report generation failed.";
        if (stderr?.includes("ValueError")) {
          errorMsg += "\nDate format mismatch. Please check your server date parser.";
        } else if (stderr?.toLowerCase().includes("webdriver")) {
          errorMsg += "\nWebDriver browser issue detected (Cloudflare challenge, etc).";
        }
        alert(errorMsg);
      }
    } catch (err) {
      console.error(err);
      alert("❌ Failed to generate report. Server error or unavailable.");
    } finally {
      setLoading(false);
    }
  };

  const handleRunReportForQueuedCaseIDs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/case/quiedCaseIds`);
      if (!res.data || !Array.isArray(res.data.data)) {
        console.log("⚠️ Unexpected response format from server.");
        return;
      }
      const queuedCases = res.data.data;
      if (queuedCases.length === 0) {
        console.log("ℹ️ No queued case IDs found.");
        return;
      }

      console.log(`✅ Found ${queuedCases.length} queued case IDs. Running report...`);
      const allResults = [];

      for (const caseObj of queuedCases) {
        const { case_id, petition_court } = caseObj;
        console.log(`🔍 Processing case ${case_id} (${petition_court})`);
        try {
          const response = await axios.post(
            `${SCRAPING_BASE_URL}/api/refreshCaseID`,
            {
              HARDCODED_DOCUMENT_CHECK_DATE_STR: "",
              KINGS_COUNTY_TEXT: petition_court,
              TARGET_FILE_NUMBERS: [case_id],
            }
          );
          allResults.push({
            case_id,
            petition_court,
            result: response.data,
          });
        } catch (err) {
          console.error(`❌ Error for case ${case_id}:`, err);
          allResults.push({
            case_id,
            petition_court,
            error: true,
          });
        }
      }

      setResults(allResults);
      setShowResultsPopup(true); // Show final results popup
      console.log(`✅ Finished processing ${allResults.length} cases.`);
    } catch (error) {
      console.error("Error fetching queued case IDs:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-md mx-auto relative mt-4">
      <h2 className="text-xl font-bold mb-6">📄 Generate Report</h2>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80">
          <div className="flex flex-col items-center">
            <div className="h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" />
            <p className="text-sm text-blue-700 font-medium">
              Loading, please wait...
            </p>
          </div>
        </div>
      )}

      {/* Confirmation Popup */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-96">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            <p>Are you sure you want to update queued case IDs?</p>
            <div className="flex justify-end mt-4 gap-3">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={() => {
                  setShowConfirm(false);
                  handleRunReportForQueuedCaseIDs();
                }}
              >
                Yes, Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Popup with Table */}
      {showResultsPopup && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl overflow-hidden">
      
      {/* Header */}
      <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
        <h3 className="text-xl font-semibold text-gray-800">Report Results</h3>
        <button
          className="text-gray-500 hover:text-gray-700"
          onClick={() => setShowResultsPopup(false)}
        >
          ✕
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="min-w-full text-sm text-gray-700">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Case ID</th>
              <th className="px-4 py-3 text-left">Court</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Message</th>
            </tr>
          </thead>
          <tbody>
            {results.map((item, index) => (
              <tr
                key={index}
                className="border-b hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-blue-600 font-medium">{item.case_id}</td>
                <td className="px-4 py-3">{item.petition_court}</td>
                <td className="px-4 py-3">
                  {item.error ? (
                    <span className="text-red-600 font-semibold">❌ Error</span>
                  ) : item.result?.success ? (
                    <span className="text-green-600 font-semibold">✅ Success</span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">⚠️ Failed</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {item.result?.message || (item.error ? "Request failed" : "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t flex justify-end bg-gray-50">
        <button
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm"
          onClick={() => setShowResultsPopup(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}


      {!showForm && (
        <div className="space-y-4">
          <button
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full"
            onClick={() => setShowConfirm(true)} // Show confirm popup first
             id="tooltip-added"
          >
            Get Update Quied Case ID
          </button>
          <Tooltip
        anchorSelect="#tooltip-added"
        place="bottom"
        content="We will Check All Case Id's for Latest Update is there role updated As ADMINISTRATOR ! "
        className="custom-tooltip"
      />
          <button
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 w-full"
            onClick={() => setShowForm(true)}
          >
            Get New Data
          </button>
        </div>
      )}

      {showForm && (
        <div className="space-y-4 mt-6">
          <input
            type="date"
            className="w-full border px-3 py-2 rounded"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <input
            type="date"
            className="w-full border px-3 py-2 rounded"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />

          <select
            className="w-full border px-3 py-2 rounded"
            value={court}
            onChange={(e) => setCourt(e.target.value)}
            required
          >
            <option value="">-- Please select court --</option>
            <option value="Queens County Surrogate's Court">
              Queens County Surrogates Court
            </option>
            <option value="Bronx County Surrogate's Court">
              Bronx County Surrogates Court
            </option>
            <option value="Kings County Surrogate's Court">
              Kings County Surrogates Court
            </option>
          </select>

          <select
            className="w-full border px-3 py-2 rounded"
            value={proceeding}
            onChange={(e) => setProceeding(e.target.value)}
            required
          >
            <option value="">-- Please select file proceeding --</option>
            <option value="Probate Petition">Probate Petition</option>
            <option value="ADMINISTRATION PETITION">
              Administration Petition
            </option>
            <option value="Voluntary Administration">
              Voluntary Administration
            </option>
            <option value="Preliminary Letters (Probate or Administration)">
              Preliminary Letters (Probate or Administration)
            </option>
          </select>

          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className={`${
              loading ? "opacity-50 cursor-not-allowed" : ""
            } bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 w-full`}
          >
            {loading ? "Processing..." : "Generate Report"}
          </button>

          <button
            className="text-sm text-gray-500 underline mt-2"
            onClick={() => setShowForm(false)}
          >
            ← Back to menu
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
